import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Heart, MapPin, ExternalLink, ChevronRight, FileText } from 'lucide-react';
import LoginModal from '@/components/LoginModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [featuredBand, setFeaturedBand] = useState(null);
  const [newestBands, setNewestBands] = useState([]);
  const [featuredBandBlog, setFeaturedBandBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await fetch(`${API}/seed`, { method: 'POST' });
        
        // Fetch featured band
        const featuredRes = await fetch(`${API}/bands/featured`);
        if (featuredRes.ok) {
          const data = await featuredRes.json();
          setFeaturedBand(data);
          
          // Fetch blog post about featured band
          if (data?.band_id) {
            const blogRes = await fetch(`${API}/blog/featured/${data.band_id}`);
            if (blogRes.ok) {
              const blogData = await blogRes.json();
              setFeaturedBandBlog(blogData);
            }
          }
        }
        
        // Fetch newest bands
        const newestRes = await fetch(`${API}/bands/newest?limit=8`);
        if (newestRes.ok) {
          const data = await newestRes.json();
          setNewestBands(data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleProtectedAction = (action, message, targetPath = null) => {
    if (isAuthenticated) {
      action();
    } else {
      setLoginMessage(message);
      setPendingNavigation(targetPath);
      setLoginModalOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
    } else {
      window.location.reload();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Login Modal */}
      <LoginModal 
        open={loginModalOpen} 
        onOpenChange={(open) => {
          setLoginModalOpen(open);
          if (!open) setPendingNavigation(null);
        }}
        message={loginMessage}
        onSuccess={handleLoginSuccess}
      />

      {/* Hero Section - Featured Band */}
      {featuredBand && (
        <section className="relative border-b-2 border-border" data-testid="featured-band-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Featured Band Info */}
              <div className="space-y-6">
                <div className="inline-block">
                  <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary border border-primary px-3 py-1">
                    Featured Band
                  </span>
                </div>
                
                <h1 className="font-accent text-5xl sm:text-6xl lg:text-7xl text-foreground leading-none">
                  {featuredBand.name}
                </h1>
                
                <div className="flex flex-wrap gap-2">
                  {featuredBand.genre_tags?.map((tag, i) => (
                    <span 
                      key={i}
                      className="font-mono text-xs uppercase px-2 py-1 border border-muted-foreground text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                {featuredBand.location && (
                  <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm">
                    <MapPin className="w-4 h-4" />
                    {featuredBand.location}
                  </div>
                )}
                
                <p 
                  className="font-mono text-base text-foreground/80 leading-relaxed max-w-lg"
                  dangerouslySetInnerHTML={{ 
                    __html: featuredBand.bio?.replace(/\n/g, '<br/>').replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '<br/><br/>') 
                  }}
                />
                
                <div className="flex items-center gap-4 pt-4">
                  <Button 
                    onClick={() => handleProtectedAction(
                      () => navigate(`/band/${featuredBand.band_id}`),
                      'Sign in to view full band profiles and listen to their music.',
                      `/band/${featuredBand.band_id}`
                    )}
                    className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase tracking-widest btn-brutal px-8 py-6 text-base"
                    data-testid="view-featured-band-btn"
                  >
                    View Band
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                  
                  <div className="flex items-center gap-2 text-secondary font-mono">
                    <Heart className="w-5 h-5 fill-current" />
                    <span>{featuredBand.heart_count}</span>
                  </div>
                </div>
                
                {/* Featured Band Blog Entry - visible to all for SEO */}
                {featuredBandBlog && (
                  <div className="mt-6 border-t border-border pt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-mono text-xs uppercase text-primary tracking-wider">Blog Feature</span>
                    </div>
                    <h3 className="font-sans text-lg font-bold text-foreground mb-2">{featuredBandBlog.title}</h3>
                    <p className="font-mono text-sm text-muted-foreground line-clamp-2">
                      {featuredBandBlog.content}
                    </p>
                    <Link 
                      to={`/blog/${featuredBandBlog.post_id}`}
                      className="inline-flex items-center gap-1 font-mono text-xs uppercase text-primary hover:underline mt-2"
                    >
                      Read More <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
                
                {/* No blog yet - prompt admin */}
                {!featuredBandBlog && (
                  <div className="mt-6 border-t border-border/50 pt-6">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <FileText className="w-4 h-4" />
                      <span className="font-mono text-xs">No blog feature yet for this band</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Featured Band Image */}
              <div className="relative">
                <div className="aspect-square border-2 border-border overflow-hidden bg-card">
                  {featuredBand.image_urls?.[0] ? (
                    <img 
                      src={featuredBand.image_urls[0]}
                      alt={featuredBand.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <span className="font-accent text-6xl text-muted-foreground">
                        {featuredBand.name[0]}
                      </span>
                    </div>
                  )}
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-primary -z-10"></div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Newest Bands Section */}
      <section className="py-16" data-testid="newest-bands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-accent text-4xl sm:text-5xl text-foreground">NEWEST BANDS</h2>
              <p className="font-mono text-sm text-muted-foreground mt-2 uppercase tracking-widest">
                Fresh talent, just approved
              </p>
            </div>
            <Button 
              variant="outline"
              onClick={() => handleProtectedAction(
                () => navigate('/browse'),
                'Sign in to browse all bands and discover more music.',
                '/browse'
              )}
              className="border-2 border-foreground text-foreground hover:bg-foreground hover:text-background font-mono uppercase tracking-widest hidden sm:flex"
              data-testid="browse-all-btn"
            >
              Browse All
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          {/* Show full band cards - visible to all */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newestBands.map((band) => (
              <div 
                key={band.band_id}
                onClick={() => handleProtectedAction(
                  () => navigate(`/band/${band.band_id}`),
                  `Sign in to view ${band.name}'s full profile and listen to their music.`,
                  `/band/${band.band_id}`
                )}
                className="group cursor-pointer"
                data-testid={`band-card-${band.band_id}`}
              >
                <div className="border-2 border-border bg-card overflow-hidden card-brutal">
                  {/* Band Image */}
                  <div className="aspect-square overflow-hidden">
                    {band.image_urls?.[0] ? (
                      <img 
                        src={band.image_urls[0]}
                        alt={band.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="font-accent text-4xl text-muted-foreground">
                          {band.name[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Band Info */}
                  <div className="p-4 border-t-2 border-border">
                    <h3 className="font-sans text-lg font-bold text-foreground group-hover:text-primary transition-colors truncate">
                      {band.name}
                    </h3>
                    
                    <div className="flex flex-wrap gap-1 mt-2">
                      {band.genre_tags?.slice(0, 2).map((tag, i) => (
                        <span 
                          key={i}
                          className="font-mono text-[10px] uppercase px-1.5 py-0.5 bg-muted text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      {band.location && (
                        <span className="font-mono text-xs text-muted-foreground truncate">
                          {band.location}
                        </span>
                      )}
                      <div className="flex items-center gap-1 text-secondary font-mono text-xs">
                        <Heart className="w-3 h-3" />
                        {band.heart_count}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Mobile Browse All Button */}
          <div className="mt-8 text-center sm:hidden">
            <Button 
              variant="outline"
              onClick={() => handleProtectedAction(
                () => navigate('/browse'),
                'Sign in to browse all bands and discover more music.',
                '/browse'
              )}
              className="border-2 border-foreground text-foreground hover:bg-foreground hover:text-background font-mono uppercase tracking-widest"
              data-testid="browse-all-mobile-btn"
            >
              Browse All Bands
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t-2 border-border py-16 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-accent text-4xl sm:text-5xl text-foreground mb-4">
                ARE YOU A BAND?
              </h2>
              <p className="font-mono text-base text-muted-foreground mb-6">
                Join the underground. Apply to be featured on Find Good Bands and get discovered by listeners who actually care about good music.
              </p>
              <Link to="/apply">
                <Button 
                  className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase tracking-widest btn-brutal px-8 py-6"
                  data-testid="apply-cta-btn"
                >
                  Apply Now
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="text-right hidden lg:block">
              <span className="font-accent text-[8rem] leading-none text-muted/20">
                ♪
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
